"use client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { SymbolIcon } from "@radix-ui/react-icons";
import { useRouter } from "next/navigation";
import * as z from "zod";
import { useToast } from "@/components/ui/use-toast";
import {
  ResetPasswordInput,
  resetPassword,
  confirmResetPassword,
  ConfirmResetPasswordInput,
} from "aws-amplify/auth";

export function ResetPasswordForm({ email }: { email?: string }) {
  return email ? (
    <ForgotPasswordResetPasswordForm email={email} />
  ) : (
    <SendCodeToEmailForm />
  );
}

function SendCodeToEmailForm() {
  const { toast } = useToast();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setIsLoading(true);
    const formData = new FormData(event.target as HTMLFormElement);

    const schema = z.object({
      email: z
        .string()
        .email({ message: "Please enter a valid email address." }),
    });

    try {
      const email = String(formData.get("email") || "");
      schema.parse({ email });
      await resetPassword({ username: email } as ResetPasswordInput);
      router.push("/reset?email=" + encodeURIComponent(email));
    } catch (err: any) {
      let message;
      switch (err.name) {
        case "ZodError":
          message = err.issues[0].message;
          break;
        case "LimitExceededException":
          message = "Attempt limit exceeded, please try after some time.";
          break;
        default:
          message = "Something went wrong. Try again later.";
          break;
      }

      toast({
        title: "Uh-oh :(",
        description: message,
        variant: "destructive",
      });

      throw err;
    }

    setIsLoading(false);
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-2">
      <div className="grid gap-2">
        <div className="grid gap-1">
          <Label className="sr-only" htmlFor="email">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            placeholder="Email"
            type="email"
            autoCapitalize="none"
            autoComplete="email"
            autoCorrect="off"
            required={true}
            disabled={isLoading}
          />
        </div>

        <Button type="submit" disabled={isLoading}>
          {isLoading && <SymbolIcon className="mr-2 h-4 w-4 animate-spin" />}
          Send Code
        </Button>
      </div>
    </form>
  );
}

function ForgotPasswordResetPasswordForm({ email }: { email?: string }) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toast } = useToast();
  const router = useRouter();

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setIsLoading(true);
    const formData = new FormData(event.target as HTMLFormElement);

    // used to validate the form
    const schema = z.object({
      email: z
        .string()
        .email({ message: "Please enter a valid email address." }),
      password: z
        .string()
        .min(8, { message: "Password must be at least 8 characters." })
        .regex(/[a-z]/, {
          message: "Password must include a lowercase letter.",
        })
        .regex(/[A-Z]/, {
          message: "Password must include an uppercase letter.",
        })
        .regex(/[0-9]/, { message: "Password must include a number." })
        .regex(/[^a-zA-Z0-9]/, {
          message: "Password must include a special character.",
        }),
      code: z.string(),
    });

    const email = String(formData.get("email") || "");
    const code = String(formData.get("code") || "");
    const password = String(formData.get("new-password") || "");

    // send reset password code via email
    try {
      schema.parse({ email, password, code });
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      } as ConfirmResetPasswordInput);
      router.push(`/login?email=${encodeURIComponent(email)}`);
    } catch (err: any) {
      let message;
      switch (err.name) {
        case "ZodError":
          message = err.issues[0].message;
          break;
        case "LimitExceededException":
          message = "Attempt limit exceeded, please try after some time.";
          break;
        case "CodeMismatchException":
          message = err.message;
          break;
        default:
          message = "Something went wrong. Try again later.";
          break;
      }

      toast({
        title: "Uh-oh :(",
        description: message,
        variant: "destructive",
      });

      throw err;
    }

    setIsLoading(false);
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-2">
      <div className="grid gap-2">
        <div className="grid gap-1">
          <Input id="email" name="email" type="hidden" defaultValue={email} />
          <Input
            id="code"
            name="code"
            placeholder="Code"
            type="text"
            required={true}
            disabled={isLoading}
          />
          <Input
            id="new-password"
            name="new-password"
            placeholder="New Password"
            type="password"
            autoComplete="new-password"
            required={true}
            disabled={isLoading}
          />
        </div>

        <Button type="submit" disabled={isLoading}>
          {isLoading && <SymbolIcon className="mr-2 h-4 w-4 animate-spin" />}
          Reset password
        </Button>
      </div>
    </form>
  );
}
